import React, { useState, useEffect, useRef } from 'react';
import { Icon, Button } from 'semantic-ui-react';
import moment from 'moment';
import Loader from '../Loader';
import generateAISuggestions from '../../services/generateAISuggestions';
import './ErrorConsoleAI.scss';

const ErrorConsoleAI = ({ isOpen, onClose, jobData }) => {
    const [suggestions, setSuggestions] = useState(null);
    const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
    const [error, setError] = useState(null);
    const consoleRef = useRef(null);

    useEffect(() => {
        if (isOpen && consoleRef.current) {
            consoleRef.current.focus();
        }

        const handleEscape = (e) => {
            if (e.key === 'Escape' && isOpen) {
                onClose();
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [isOpen, onClose]);

    const handleGenerateSuggestions = async () => {
        try {
            setIsLoadingSuggestions(true);
            setError(null);

            const payload = {
                jobName: jobData.jobName,
                lastError: (jobData.state && jobData.state.error) || 'Unknown error',
                lastFailureAt: jobData.state && jobData.state.lastFailureAt,
                lastSuccessAt: jobData.state && jobData.state.lastSuccessAt,
                consecutiveFailures: (jobData.metrics && jobData.metrics.consecutiveFailures) || 0,
                totalRuns: (jobData.metrics && jobData.metrics.totalRuns) || 0,
                successRate: (jobData.metrics && jobData.metrics.successRate) || 0
            };

            const response = await generateAISuggestions(payload);
            
            if (response.data && response.data.suggestions) {
                setSuggestions(response.data.suggestions);
            } else {
                setError('No suggestions received from AI');
            }
        } catch (err) {
            console.error('Error generating AI suggestions:', err);
            setError((err.response && err.response.data && err.response.data.message) || 'Failed to generate suggestions. Please try again.');
        } finally {
            setIsLoadingSuggestions(false);
        }
    };

    const formatDate = (date) => {
        if (!date) return 'Never';
        return moment(date).format('DD MMM YYYY, HH:mm:ss');
    };

    const formatRelativeTime = (date) => {
        if (!date) return '';
        return moment(date).fromNow();
    };

    if (!isOpen) return null;

    return (
        <>
            <div className="error-console-overlay" onClick={onClose} />
            <div 
                className={`error-console-ai ${isOpen ? 'open' : ''}`}
                ref={consoleRef}
                tabIndex={-1}
            >
                <div className="console-header">
                    <div className="header-content">
                        <Icon name="bug" size="large" />
                        <h2>Error Console AI</h2>
                    </div>
                    <Icon 
                        name="close" 
                        size="large" 
                        link 
                        onClick={onClose}
                        className="close-icon"
                    />
                </div>

                <div className="console-body">
                    {/* Job Information Section */}
                    <div className="info-section">
                        <div className="section-title">
                            <Icon name="briefcase" />
                            Job Information
                        </div>
                        <div className="info-card">
                            <div className="info-item">
                                <span className="label">Job Name:</span>
                                <span className="value">{jobData.jobName}</span>
                            </div>
                            <div className="info-item">
                                <span className="label">Current Status:</span>
                                <span className={`value status-badge ${(jobData.state && jobData.state.currentStatus) === 'success' ? 'success' : 'failure'}`}>
                                    <Icon name={(jobData.state && jobData.state.currentStatus) === 'success' ? 'check circle' : 'times circle'} />
                                    {(jobData.state && jobData.state.currentStatus) || 'Unknown'}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Execution Timeline Section */}
                    <div className="info-section">
                        <div className="section-title">
                            <Icon name="clock outline" />
                            Execution Timeline
                        </div>
                        <div className="timeline-grid">
                            <div className="timeline-item success">
                                <div className="timeline-icon">
                                    <Icon name="check circle" />
                                </div>
                                <div className="timeline-content">
                                    <div className="timeline-label">Last Success</div>
                                    <div className="timeline-date">
                                        {formatDate(jobData.state && jobData.state.lastSuccessAt)}
                                    </div>
                                    {jobData.state && jobData.state.lastSuccessAt && (
                                        <div className="timeline-relative">
                                            {formatRelativeTime(jobData.state.lastSuccessAt)}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="timeline-item failure">
                                <div className="timeline-icon">
                                    <Icon name="times circle" />
                                </div>
                                <div className="timeline-content">
                                    <div className="timeline-label">Last Failure</div>
                                    <div className="timeline-date">
                                        {formatDate(jobData.state && jobData.state.lastFailureAt)}
                                    </div>
                                    {jobData.state && jobData.state.lastFailureAt && (
                                        <div className="timeline-relative">
                                            {formatRelativeTime(jobData.state.lastFailureAt)}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Error Details Section */}
                    <div className="info-section">
                        <div className="section-title">
                            <Icon name="exclamation triangle" />
                            Error Details
                        </div>
                        <div className="error-details-card">
                            <div className="error-message">
                                {(jobData.state && jobData.state.lastFailureError) || 'No error message available'}
                            </div>
                            <div className="error-stats">
                                <div className="stat-item">
                                    <Icon name="redo" />
                                    <span className="stat-label">Total Runs:</span>
                                    <span className="stat-value">{(jobData.metrics && jobData.metrics.totalRuns) || 0}</span>
                                </div>
                                <div className="stat-item">
                                    <Icon name="warning sign" />
                                    <span className="stat-label">Consecutive Failures:</span>
                                    <span className="stat-value failure-count">
                                        {(jobData.metrics && jobData.metrics.consecutiveFailures) || 0}
                                    </span>
                                </div>
                                <div className="stat-item">
                                    <Icon name="chart line" />
                                    <span className="stat-label">Success Rate:</span>
                                    <span className="stat-value">
                                        {(jobData.metrics && jobData.metrics.successRate) ? `${jobData.metrics.successRate}%` : 'N/A'}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* AI Suggestions Section */}
                    {suggestions && (
                        <div className="info-section suggestions-section">
                            <div className="section-title">
                                <Icon name="lightbulb outline" />
                                AI Suggestions
                            </div>
                            <div className="suggestions-card">
                                <div className="suggestions-content">
                                    {suggestions}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Error State */}
                    {error && (
                        <div className="error-state">
                            <Icon name="warning circle" size="large" />
                            <p>{error}</p>
                        </div>
                    )}
                </div>

                {/* Footer with AI Button */}
                <div className="console-footer">
                    <Button
                        primary
                        fluid
                        size="large"
                        onClick={handleGenerateSuggestions}
                        disabled={isLoadingSuggestions}
                        className="generate-suggestions-btn"
                    >
                        {isLoadingSuggestions ? (
                            <>
                                <Icon loading name="spinner" />
                                Generating AI Suggestions...
                            </>
                        ) : (
                            <>
                                <Icon name="magic" />
                                Generate AI Suggestions
                            </>
                        )}
                    </Button>
                </div>
            </div>
        </>
    );
};

export default ErrorConsoleAI;
